import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Input } from "./ui/input";

import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Gauge, Save } from "lucide-react";

// NEXUS — "SLA por prioridade": prazo de INÍCIO e de CONCLUSÃO para cada
// uma das 4 prioridades da ficha, por TENANT. Recurso exclusivo do MODELO
// OPERACIONAL — quem renderiza este componente já checou isso
// (Configuracoes.jsx), e o backend recusa com 403 de qualquer forma.
//
// UX corrigida: cada prazo é uma DURAÇÃO — [valor] [unidade] (15 minutos,
// 4 horas, 2 dias) — nunca mais um input de horário HH:MM, que sugeria
// "hora do dia" em vez de "quanto tempo". A normalização para minutos é
// feita no BACKEND (fonte única); aqui só se envia valor + unidade.
//
// Nesta fase o SLA é SEMPRE tempo corrido 24x7 (1 dia = 24 horas), sem
// horário comercial, feriado, pausa ou SLA por cliente/contrato. Nenhum
// prazo padrão é sugerido pelo código: enquanto o Admin não configurar, as
// fichas simplesmente ficam sem SLA, em vez de herdarem um número
// inventado. O backend congela a regra vigente no momento da criação de
// cada ficha (snapshot), então alterar os prazos aqui NUNCA altera fichas
// já criadas — só vale para as próximas.
const PRIORIDADES = [
  { key: "baixa", label: "Baixa", badge: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200" },
  { key: "media", label: "Média", badge: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200" },
  { key: "alta", label: "Alta", badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" },
  { key: "urgente", label: "Urgente", badge: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200" },
];

const UNIDADES = [
  { valor: "minutos", label: "minutos" },
  { valor: "horas", label: "horas" },
  { valor: "dias", label: "dias" },
];

const CAMPOS = ["inicio", "conclusao"];

const vazio = () =>
  Object.fromEntries(PRIORIDADES.map(p => [p.key, {
    inicio_valor: "", inicio_unidade: "horas",
    conclusao_valor: "", conclusao_unidade: "horas",
  }]));

/** Converte o que o backend devolve (minutos + valor/unidade legível) para o formulário. */
function paraFormulario(prazos) {
  const out = vazio();
  PRIORIDADES.forEach(({ key }) => {
    const p = prazos?.[key];
    if (!p) return;
    out[key] = {
      inicio_valor: String(p.inicio?.valor ?? ""),
      inicio_unidade: p.inicio?.unidade || "horas",
      conclusao_valor: String(p.conclusao?.valor ?? ""),
      conclusao_unidade: p.conclusao?.unidade || "horas",
    };
  });
  return out;
}

const PESO = { minutos: 1, horas: 60, dias: 60 * 24 };
const emMinutos = (valor, unidade) => Number(valor) * (PESO[unidade] || 1);

export default function SlaConfig() {
  const [form, setForm] = useState(vazio());
  const [erros, setErros] = useState({});
  const [configurado, setConfigurado] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/config/sla");
      setConfigurado(!!data.configurado);
      setForm(paraFormulario(data.prazos));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setCampo = (prioridade, campo, valor) => {
    setForm(f => ({ ...f, [prioridade]: { ...f[prioridade], [campo]: valor } }));
    // Corrigir o campo limpa o próprio erro imediatamente — a marcação
    // nunca fica "presa" depois que a pessoa já arrumou o valor.
    setErros(e => {
      const chave = `${prioridade}.${campo.split("_")[0]}`;
      if (!e[chave]) return e;
      const { [chave]: _removido, ...resto } = e;
      return resto;
    });
  };

  /** Validação POR CAMPO — cada problema fica marcado no campo exato, com a
   *  mensagem embaixo dele. Nada de um toast genérico para um erro que dá
   *  para apontar na tela. */
  const validar = () => {
    const novos = {};
    for (const { key, label } of PRIORIDADES) {
      const linha = form[key];
      for (const campo of CAMPOS) {
        const valor = linha[`${campo}_valor`];
        const nome = campo === "inicio" ? "início" : "conclusão";
        if (valor === "" || valor === null) {
          novos[`${key}.${campo}`] = `Informe o prazo de ${nome}.`;
        } else if (!Number.isInteger(Number(valor)) || Number(valor) <= 0) {
          novos[`${key}.${campo}`] = `O prazo de ${nome} precisa ser um número inteiro maior que zero.`;
        } else if (!PESO[linha[`${campo}_unidade`]]) {
          novos[`${key}.${campo}`] = `Escolha a unidade do prazo de ${nome}.`;
        }
      }
      if (!novos[`${key}.inicio`] && !novos[`${key}.conclusao`]) {
        const ini = emMinutos(linha.inicio_valor, linha.inicio_unidade);
        const fim = emMinutos(linha.conclusao_valor, linha.conclusao_unidade);
        if (fim < ini) {
          novos[`${key}.conclusao`] = `A conclusão não pode ser menor que o início (prioridade ${label}).`;
        }
      }
    }
    return novos;
  };

  const salvar = async () => {
    const novos = validar();
    setErros(novos);
    if (Object.keys(novos).length) return;   // erro já está marcado no campo

    const body = Object.fromEntries(PRIORIDADES.map(({ key }) => [key, {
      inicio_valor: Number(form[key].inicio_valor),
      inicio_unidade: form[key].inicio_unidade,
      conclusao_valor: Number(form[key].conclusao_valor),
      conclusao_unidade: form[key].conclusao_unidade,
    }]));

    setSaving(true);
    try {
      const { data } = await api.put("/config/sla", body);
      setConfigurado(true);
      setForm(paraFormulario(data.prazos));
      toast.success("SLA por prioridade salvo. Vale para as próximas fichas — as já criadas mantêm o prazo congelado.");
    } catch (e) {
      // O backend também valida coerência e devolve prioridade/campo — quando
      // vier assim, marca o campo em vez de mostrar só um aviso solto.
      const det = e?.response?.data?.detail;
      if (det?.prioridade && det?.campo) {
        setErros({ [`${det.prioridade}.${det.campo}`]: det.mensagem });
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setSaving(false); }
  };

  const celula = (key, campo) => {
    const chave = `${key}.${campo}`;
    const erro = erros[chave];
    return (
      <td className="px-3 py-2 align-top">
        <div className="flex gap-2">
          <Input
            type="number" min="1" step="1"
            aria-invalid={erro ? "true" : undefined}
            className={`max-w-[92px] ${erro ? "border-red-500 focus-visible:ring-red-500" : ""}`}
            value={form[key][`${campo}_valor`]}
            onChange={e => setCampo(key, `${campo}_valor`, e.target.value)}
            data-testid={`sla-${key}-${campo}-valor`}
          />
          <Select
            value={form[key][`${campo}_unidade`]}
            onValueChange={v => setCampo(key, `${campo}_unidade`, v)}
          >
            <SelectTrigger className="max-w-[130px]" data-testid={`sla-${key}-${campo}-unidade`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {UNIDADES.map(u => <SelectItem key={u.valor} value={u.valor}>{u.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {erro && <p className="text-xs text-red-600 mt-1" data-testid={`sla-erro-${key}-${campo}`}>{erro}</p>}
      </td>
    );
  };

  return (
    <Card className="p-5 rounded-2xl shadow-sm space-y-4" data-testid="sla-config">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="font-heading font-semibold text-slate-900 flex items-center gap-2">
            <Gauge size={18} className="text-primary" /> SLA por prioridade
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Quanto tempo cada prioridade tem para <b>começar</b> e para <b>concluir</b>, em tempo corrido (24x7 — 1 dia = 24 horas).
          </p>
        </div>
        <Badge className={configurado ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} data-testid="sla-config-status">
          {configurado ? "Configurado" : "Não configurado"}
        </Badge>
      </div>

      {!configurado && !loading && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
          Enquanto o SLA não for configurado, as fichas novas ficam <b>sem SLA</b> — o LOOTUS nunca assume um prazo por conta própria.
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-400">Carregando...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="py-2.5 px-3">Prioridade</th>
                <th className="px-3">Prazo para início</th>
                <th className="px-3">Prazo para conclusão</th>
              </tr>
            </thead>
            <tbody>
              {PRIORIDADES.map(({ key, label, badge }) => (
                <tr key={key} className="border-t border-slate-100" data-testid={`sla-row-${key}`}>
                  <td className="py-2.5 px-3 align-top"><Badge className={badge}>{label}</Badge></td>
                  {celula(key, "inicio")}
                  {celula(key, "conclusao")}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={salvar} disabled={saving || loading} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="sla-salvar">
          <Save size={16} /> {saving ? "Salvando..." : "Salvar SLA"}
        </Button>
      </div>
    </Card>
  );
}
